import { Injectable } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore, DatastoreCollection } from '@freelancer/datastore';
import {
  PostJobPageFormState,
  PostJobPageFormStateCollection,
} from '@freelancer/datastore/collections';
import { LocalStorage } from '@freelancer/local-storage';
import { isDefined, toNumber } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { filter, map, switchMap, switchMapTo, take, tap } from 'rxjs/operators';
import {
  DraftRemoveResponse,
  DraftSaveResponse,
  DraftStatus,
} from './drafts.types';

@Injectable({
  providedIn: 'root',
})
export class Drafts {
  readonly localStorageKey = 'postJobPageDraft';

  private formStateCollection: DatastoreCollection<
    PostJobPageFormStateCollection
  >;
  private isInitialized = false;
  private localStorageDraft$: Rx.Observable<PostJobPageFormState>;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private localStorage: LocalStorage,
  ) {}

  private init(): void {
    this.formStateCollection = this.datastore.collection<
      PostJobPageFormStateCollection
    >('postJobPageFormState', query =>
      query.where(
        'userId',
        '==',
        this.auth.getUserId().pipe(map(userId => toNumber(userId))),
      ),
    );

    this.localStorageDraft$ = this.localStorage
      .getObservable(this.localStorageKey, true)
      .pipe(filter(isDefined));

    this.isInitialized = true;
  }

  /**
   * Gets the most recent draft.
   *
   * This prefers the draft on the server, but if there's no draft on the server
   * or if the request to the server fails we fall back to the version in
   * local storage.
   *
   * If there is a version in local storage and the user is logged in then we
   * move the draft from local storage to the backend.
   *
   * If there is no draft both on the server and local storage, and the user is
   * logged in, we still fall back to the local storage as our data stream.
   */
  get(): Rx.Observable<PostJobPageFormState | undefined> {
    if (!this.isInitialized) {
      this.init();
    }

    const serverDraft$ = this.formStateCollection.valueChanges().pipe(
      map(states => (states.length > 0 ? states[0] : undefined)),
      tap(() => this.localStorage.set(this.localStorageKey, null, true)),
    );

    const fallbackDraft$ = Rx.combineLatest([
      this.localStorageDraft$,
      this.auth.getUserId(),
    ]).pipe(
      tap(([draft, userId]) =>
        this.formStateCollection.set(userId, draft).then(response => {
          if (response.status === 'success') {
            this.localStorage.set(this.localStorageKey, null, true);
          }
        }),
      ),
      map(([draft]) => draft),
    );

    const ifFetchError$ = this.formStateCollection.status$.pipe(
      filter(status => isDefined(status.error)),
      switchMapTo(fallbackDraft$),
    );

    const else$ = this.formStateCollection.status$.pipe(
      filter(status => status.ready && !isDefined(status.error)),
      switchMapTo(this.formStateCollection.valueChanges()),
      switchMap(serverDrafts =>
        serverDrafts.length === 0 ? fallbackDraft$ : serverDraft$,
      ),
    );

    return this.auth.isLoggedIn().pipe(
      switchMap(isLoggedIn => {
        if (!isLoggedIn) {
          return this.localStorageDraft$;
        }

        return Rx.merge(ifFetchError$, else$);
      }),
    );
  }

  save(draftData: PostJobPageFormState): Promise<DraftSaveResponse> {
    if (!this.isInitialized) {
      this.init();
    }

    return this.auth
      .isLoggedIn()
      .pipe(
        take(1),
        switchMap(isLoggedIn => {
          if (!isLoggedIn) {
            this.localStorage.set(this.localStorageKey, draftData, true);

            return Promise.resolve({ status: 'success' } as const);
          }

          return this.auth
            .getUserId()
            .pipe(
              switchMap(userId =>
                this.formStateCollection.set(userId, draftData),
              ),
            );
        }),
      )
      .toPromise();
  }

  /**
   * Regardless of user auth state, this always deletes the draft
   * stored in local storage for a consistent logged in user experience.
   *
   * If the user is logged in, we always try to remove the draft
   * and fail silently if there is no draft to remove.
   */
  remove(): Promise<DraftRemoveResponse> {
    if (!this.isInitialized) {
      this.init();
    }

    return this.auth
      .isLoggedIn()
      .pipe(
        take(1),
        switchMap(isLoggedIn => {
          this.localStorage.set(this.localStorageKey, null, true);

          if (!isLoggedIn) {
            return Promise.resolve({ status: 'success' } as const);
          }

          return this.auth
            .getUserId()
            .pipe(
              switchMap(userId =>
                this.formStateCollection
                  .remove(userId)
                  .catch(() => Promise.resolve({ status: 'success' } as const)),
              ),
            );
        }),
      )
      .toPromise();
  }

  status(): Rx.Observable<DraftStatus> {
    if (!this.isInitialized) {
      this.init();
    }

    return this.auth.isLoggedIn().pipe(
      take(1),
      switchMap(isLoggedIn => {
        if (!isLoggedIn) {
          return Rx.of({ ready: true });
        }

        return this.formStateCollection.status$;
      }),
    );
  }
}
